/**
 * Governance Scenarios Service
 * 
 * Prioriza dados reais do backend.
 * Se a API falhar, usa fallback local MARCADO EXPLICITAMENTE.
 * Nunca mistura dados reais com fallback sem aviso.
 */
import { AZURE } from './api';
import { BASELINE_SCENARIO_KEY, DATA_SOURCE } from '../constants/governance';

const BASE = '/governance/finance/planning';

/**
 * Fetch scenarios from backend API.
 * Returns { source, baseline_key, scenarios[] }
 */
export const fetchScenariosFromAPI = async () => {
  try {
    const { data } = await AZURE.get(`${BASE}/scenarios`);
    const scenarios = data?.data || data?.scenarios || data || [];
    return {
      source: DATA_SOURCE.API,
      baseline_key: BASELINE_SCENARIO_KEY,
      scenarios: Array.isArray(scenarios) ? scenarios : [],
    };
  } catch (error) {
    console.warn('[GovernanceScenarios] API unavailable, returning empty:', error?.message);
    return {
      source: DATA_SOURCE.EMPTY,
      baseline_key: BASELINE_SCENARIO_KEY,
      scenarios: [],
      error: error?.message || 'API unavailable',
    };
  }
};

/**
 * Fetch DRE (Income Statement) for a given scenario
 */
export const fetchDREForScenario = async (scenarioKey = BASELINE_SCENARIO_KEY, months = 12) => {
  try {
    const { data } = await AZURE.get(`${BASE}/dre-detailed`, {
      params: { scenario: scenarioKey, months, show_real: true }
    });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] DRE API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};

/**
 * Fetch Cashflow for a given scenario
 */
export const fetchCashflowForScenario = async (scenarioKey = BASELINE_SCENARIO_KEY, months = 12) => {
  try {
    const { data } = await AZURE.get(`${BASE}/cashflow-detailed`, {
      params: { scenario: scenarioKey, months, show_real: true }
    });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] Cashflow API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};

/**
 * Fetch Investor Summary (consolidated read-model)
 * Returns: unit economics, runway, product projections, DRE/cashflow availability flags
 */
export const fetchInvestorSummary = async (scenarioKey = BASELINE_SCENARIO_KEY) => {
  try {
    const { data } = await AZURE.get(`${BASE}/investor-summary`, {
      params: { scenario_key: scenarioKey }
    });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] Investor Summary API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};

/**
 * Fetch Product Projections (Swim Vision, etc.)
 */
export const fetchProductProjections = async (product = null, scenarioKey = BASELINE_SCENARIO_KEY, months = 12) => {
  try {
    const params = { scenario: scenarioKey, months };
    if (product) params.product = product;
    const { data } = await AZURE.get(`${BASE}/product-projections`, { params });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] Product Projections API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};

/**
 * Fetch Runway calculation
 */
export const fetchRunway = async (scenarioKey = BASELINE_SCENARIO_KEY) => {
  try {
    const { data } = await AZURE.get(`${BASE}/runway`, {
      params: { scenario: scenarioKey }
    });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] Runway API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};

// ============ SWIMVISION EXECUTIVE (Entrega 2) ============

/**
 * Fetch SwimVision DRE (Income Statement by Product)
 * Returns: receita bruta, impostos, receita líquida, royalties, custos variáveis, margem bruta
 */
export const fetchSwimVisionDRE = async (scenarioKey = BASELINE_SCENARIO_KEY, months = 12) => {
  try {
    const { data } = await AZURE.get(`${BASE}/product-projections/swimvision/dre`, {
      params: { scenario_key: scenarioKey, months }
    });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] SwimVision DRE API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};

/**
 * Fetch SwimVision Cashflow (Cashflow Statement by Product)
 * Returns: recebimentos, pagamentos (royalties, Azure, fixos), saldo, burn, runway
 */
export const fetchSwimVisionCashflow = async (scenarioKey = BASELINE_SCENARIO_KEY, months = 12) => {
  try {
    const { data } = await AZURE.get(`${BASE}/product-projections/swimvision/cashflow`, {
      params: { scenario_key: scenarioKey, months }
    });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] SwimVision Cashflow API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};

/**
 * Fetch SwimVision Partner View (Commercial view without sensitive internal costs)
 * Returns: receita total, receita parceiro, volume clubes/atletas/vídeos, preço médio
 */
export const fetchSwimVisionPartnerView = async (scenarioKey = BASELINE_SCENARIO_KEY, months = 12) => {
  try {
    const { data } = await AZURE.get(`${BASE}/product-projections/swimvision/partner-view`, {
      params: { scenario_key: scenarioKey, months }
    });
    return { source: DATA_SOURCE.API, data: data?.data || data };
  } catch (error) {
    console.warn('[GovernanceScenarios] SwimVision Partner View API error:', error?.message);
    return { source: DATA_SOURCE.EMPTY, data: null, error: error?.message };
  }
};
